import { useCallback, useEffect, useState } from 'react';
import { examLibraryApi, apiErrorMessage } from '../services/api';
import { useOrg } from '../context/OrgContext';
import { normalizeExamStructure } from '../utils/examStructure';

// Module-level cache so navigating between the structure view, the question
// bank and the screener does not refetch the same exam hierarchy. Keyed by org
// as well as exam: the structure route is scoped to the org's enrolment.
const cache = new Map(); // `${orgId}:${examId}` -> { sections, countsByTopic, totalQuestions, topicCount }

async function fetchHierarchy(orgId, examId) {
  const res = await examLibraryApi.getStructure(orgId, examId);
  return normalizeExamStructure(res.data);
}

export default function useExamHierarchy(examId) {
  const org = useOrg();
  const orgId = org?.id || null;
  const key = orgId && examId ? `${orgId}:${examId}` : null;

  const [version, setVersion] = useState(0);
  const [state, setState] = useState(() => ({
    key,
    data: key ? cache.get(key) || null : null,
    loading: Boolean(key && !cache.has(key)),
    error: null
  }));

  useEffect(() => {
    if (!key) {
      setState({ key, data: null, loading: false, error: null });
      return undefined;
    }
    if (cache.has(key)) {
      setState({ key, data: cache.get(key), loading: false, error: null });
      return undefined;
    }
    let cancelled = false;
    setState({ key, data: null, loading: true, error: null });
    fetchHierarchy(orgId, examId)
      .then((data) => {
        cache.set(key, data);
        if (!cancelled) setState({ key, data, loading: false, error: null });
      })
      .catch((err) => {
        if (!cancelled) setState({ key, data: null, loading: false, error: apiErrorMessage(err, 'Failed to load exam structure') });
      });
    return () => { cancelled = true; };
  }, [key, orgId, examId, version]);

  const reload = useCallback(() => {
    if (key) cache.delete(key);
    setVersion((v) => v + 1);
  }, [key]);

  // Ignore state belonging to a previous exam while the new one loads.
  const stale = state.key !== key;
  const data = stale ? null : state.data;

  return {
    sections: data?.sections || [],
    countsByTopic: data?.countsByTopic || {},
    totalQuestions: data?.totalQuestions ?? 0,
    topicCount: data?.topicCount ?? 0,
    loading: stale ? Boolean(key) : state.loading,
    error: stale ? null : state.error,
    reload
  };
}
